import React, { Suspense, useEffect, useState } from "react"
import _ from "lodash"
import Color from "color"
import styled from "styled-components"
import graphql from "babel-plugin-relay/macro"
import {
  useQueryLoader,
  usePreloadedQuery,
} from "react-relay"

import { LoadingMask } from "../../CommonStyles"
import { useOutletContext } from "react-router-dom"
const colorNormal = "rgb(251, 251, 251)"
const colorLight = "rgb(253, 253, 253)"
const colorShadow = "rgba(0, 0, 0, 0.2)"

const LUM_THRESHOLD = 0.6
const ROUTER_BASE = process.env.REACT_APP_ROUTER_BASE || ""

const LoadingCircle = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 72px;
  height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;

  .sphere{
    background: #fff;
    border-radius: 50%;
    position: absolute;
    
    &:nth-child(1){
      height: 100%;
      width: 100%;
      z-index: 1;
      box-shadow: 3px 3px 10px 0px ${colorShadow};
      animation: bob-large 2s ease 1s infinite;
    }
    &:nth-child(2){
      height: 66%;
      width: 66%;
      z-index: 2;
      box-shadow: 4px 4px 10px 0px ${colorShadow};
      animation: bob 2s ease 500ms infinite;
    }
    &:nth-child(3){
      height: 33%;
      width: 33%;
      z-index: 3;
      box-shadow: 4px 4px 10px 0px ${colorShadow};
      animation: bob 2s ease infinite;
    }
  }

  @keyframes bob{
    0%{
      box-shadow: 4px 4px 10px 0px ${colorShadow};
      background: ${colorLight};
    }
    50%{
      box-shadow: 1px 1px 2px 0px ${colorShadow};
      background: ${colorNormal};
    }
    100%{
      box-shadow: 4px 4px 10px 0px ${colorShadow};
      background: ${colorLight};
    }
  }

  @keyframes bob-large{
    0%{
      box-shadow: 3px 3px 10px 0px ${colorShadow};
      background: ${colorLight};
    }
    50%{
      box-shadow: 1px 1px 8px 0px ${colorShadow};
      background: ${colorNormal};
    }
    100%{
      box-shadow: 3px 3px 10px 0px ${colorShadow};
      background: ${colorLight};
    }
  }
`

const WebChatCircle = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 3px 3px 10px 0px ${colorShadow};
  background: center no-repeat;
  background-color: #fff;
  background-image: ${props => `url("${props.backgroundImage}")`};
  background-size: contain;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;

  &.disable {
    pointer-events: none;
  }
`
const CloseCircle = styled(WebChatCircle)`
  background-color: ${props => props.color};
  background-image: none;
  z-index: 10;
  pointer-events: none;
  transform: scale(0);
  transition: all 150ms cubic-bezier(1,.74,.58,1);

  &.open {
    pointer-events: all;
    transform: scale(1);
  }
`

const CloseIcon = styled.div`
  width: 24px;
  height: 24px;
  background: center no-repeat;
  background-image: ${(props) => {
    const bg = Color(props.color)
    if (bg.luminosity() > LUM_THRESHOLD) {
      return `url('${ROUTER_BASE}/close_dark.png')`
    }
    return `url('${ROUTER_BASE}/close_light.png')`
  }};
  background-size: contain;
`

const WebchatButtonQuery = graphql`
  query WebchatButtonQuery {
    customerViewer {
      id
      businessId
      orgId
      profile {
        logoUrl
        botAgentImageUrl
      }
      chatConfiguration {
        themeColor
      }
    }
  }
`

const InnerWebchatButton = ({
  queryRef,
  postMessageToSDK,
  ...props
}) => {
  const [openWebchat, setOpenWebchat] = useState(false)

  const handleEvent = (e) => {
    if (e?.data?.source === "woztell-web-chat-parent") {
      const payload = e.data.payload
      switch (payload.event) {
        case "INIT": {
          const {
            defaultOpen = false,
          } = payload.data
          if (defaultOpen) {
            setOpenWebchat(defaultOpen)
          }
          break
        }
        case "OPEN_WEBCHAT":
          setOpenWebchat(true)
          break
        case "CLOSE_WEBCHAT":
          setOpenWebchat(false)
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    postMessageToSDK({
      event: "BUTTON_READY"
    })
    window.addEventListener("message", handleEvent)

    return () => {
      window.removeEventListener("message", handleEvent)
    }
  }, [])

  const data = usePreloadedQuery(
    WebchatButtonQuery,
    queryRef,
  )
  const profile = data?.customerViewer?.profile
  if (!profile) {
    return (
      <LoadingCircle>
        <div className="sphere"></div>
        <div className="sphere"></div>
        <div className="sphere"></div>
      </LoadingCircle>
    )
  }

  const color = _.get(data, "customerViewer.chatConfiguration.themeColor", "#173753")
  return (
    <div>
      <WebChatCircle
        color={color}
        className={openWebchat ? "disable" : ""}
        backgroundImage={profile?.logoUrl}
        onClick={() => {
          postMessageToSDK({
            event: "OPEN_WEBCHAT"
          })
        }}
      />
      <CloseCircle
        color={color}
        className={openWebchat ? "open" : ""}
        onClick={() => {
          postMessageToSDK({
            event: "CLOSE_WEBCHAT"
          })
        }}
      >
        <CloseIcon
          color={color}
        />
      </CloseCircle>
    </div>
  )
}

const WebchatButton = ({
  cookies,
  ...props
}) => {
  const {
    postMessageToSDK
  } = useOutletContext()
  const [
    queryReference,
    loadQuery,
  ] = useQueryLoader(
    WebchatButtonQuery,
    props.initialQueryRef, /* e.g. provided by router */
  )

  useEffect(() => {
    if (navigator.userAgent !== "ReactSnap") {
      loadQuery()
    }
  }, [])

  if (queryReference) {
    return (
      <Suspense fallback={
        <LoadingMask>
          <div className="line"></div>
          <div className="line"></div>
          <div className="line"></div>
        </LoadingMask>
      }>
        <InnerWebchatButton
          queryRef={queryReference}
          postMessageToSDK={postMessageToSDK}
        />
      </Suspense>
    )
  }
}

export default WebchatButton
